import { Navigate } from "react-router-dom";
import { GoogleLogo, ShieldCheck, ChartLineUp, FileArrowUp } from "@phosphor-icons/react";
import { Wordmark } from "@/components/Wordmark";
import { useAuth } from "@/context/AuthContext";
import { FullScreenLoader } from "@/components/FullScreenLoader";

const FEATURES = [
  { icon: FileArrowUp, text: "AI extraction from Government BOQ PDF, Excel & CSV" },
  { icon: ChartLineUp, text: "Item-wise & project-wise profit with deduction and GST" },
  { icon: ShieldCheck, text: "Private, cloud-synced projects for your account" },
];

export default function Login() {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (user) return <Navigate to="/projects" replace />;

  const signIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/projects";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.15fr_1fr] bg-white">
      <div className="relative hidden lg:block overflow-hidden">
        <img src="/login-bg.jpg" alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-slate-900/75" />
        <div className="absolute inset-0 blueprint-grid opacity-30" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <Wordmark inverted size="md" />
          <div className="max-w-lg rise-in">
            <p className="text-xs uppercase tracking-[0.25em] text-emerald-300 font-semibold mb-4">Construction Estimation Suite</p>
            <h1 className="font-heading text-5xl font-black leading-[1.02] tracking-tight">Know your margin before you break ground.</h1>
            <p className="mt-5 text-slate-300 text-base leading-relaxed">
              Compare Government-approved BOQ rates against real market cost, apply tender deductions and GST, and see exactly where every rupee of profit comes from.
            </p>
          </div>
          <div className="text-xs text-slate-400">EF INFRA · Jammu & Kashmir, India</div>
        </div>
      </div>

      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-16 py-12 blueprint-grid lg:bg-none">
        <div className="w-full max-w-md rise-in">
          <div className="lg:hidden mb-10"><Wordmark size="lg" /></div>
          <p className="text-xs uppercase tracking-[0.25em] text-slate-400 font-semibold">Secure Access</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-2">Sign in to EF INFRA</h2>
          <p className="text-sm text-slate-500 mt-3">Use your Google account to access your BOQ projects across Mac, iPhone, iPad and Android.</p>

          <button
            data-testid="google-login-button"
            onClick={signIn}
            className="mt-8 w-full inline-flex items-center justify-center gap-3 h-12 bg-slate-900 text-white font-medium rounded-sm hover:bg-slate-800 transition-colors shadow-sm"
          >
            <GoogleLogo size={20} weight="bold" /> Continue with Google
          </button>

          <ul className="mt-10 space-y-4 border-t border-slate-200 pt-8">
            {FEATURES.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-start gap-3 text-sm text-slate-600">
                <span className="h-8 w-8 shrink-0 rounded-sm bg-emerald-50 border border-emerald-200 text-emerald-700 flex items-center justify-center">
                  <Icon size={18} weight="duotone" />
                </span>
                <span className="pt-1.5">{text}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
